import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Silent } from "@/__main__/data-model.mjs";

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const FactorResult = {
  patch: String,
  queue_id: Number,
  tier: String,
  region: String,
  factors: [String],
  filters: String,
  win_rate: Number,
  champions: [
    {
      improvement_win_rate: Number,
      win_rate: Number,
      diff_win_rate: Number,
      individual_position: String,
      champion_id: Number,
      games: Number,
      factors_champion_score: Number,
    },
  ],
} satisfies ModelDefinition;

const FactorsStatsModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [[Silent(String)]],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([FactorResult], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const factors = list.map((row) => {
    const result = {
      patch: "",
      queue_id: 0,
      tier: "",
      region: "",
      factors: [],
      filters: "",
      win_rate: 0,
      champions: [],
    };

    for (const column of columns) {
      if (!result.hasOwnProperty(column.name)) continue;
      const value: string = (row[column.position] || "") as unknown as string;
      result[column.name] = castType(column.typeName, value);
    }

    return result;
  });

  return factors;
});

export default FactorsStatsModel;

export type FactorsStats = FromModel<typeof FactorsStatsModel>;
export type FactorResult = FromModel<typeof FactorResult>;

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "MAP":
      return value ? recursiveJsonParse(value) : {};
    case "ARRAY":
      return value ? recursiveJsonParse(value) : [];
    case "STRUCT":
      return value ? recursiveJsonParse(value) : {};
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function recursiveJsonParse(input: any): any {
  // Base case 1: If input is null or undefined, return as is
  if (input === null || input === undefined) {
    return input;
  }

  // Base case 2: If input is a string, try to parse it
  if (typeof input === "string") {
    try {
      // Attempt to parse the string as JSON
      const parsed = JSON.parse(input);

      // If successfully parsed and result is an object or array,
      // recursively parse its contents
      if (parsed && (typeof parsed === "object" || Array.isArray(parsed))) {
        return recursiveJsonParse(parsed);
      }

      // If parsed result is a primitive, return it directly
      return parsed;
    } catch (_e) {
      // If parsing fails, it's not a valid JSON string, so return as is
      return input;
    }
  }

  // Case 3: If input is an array, recursively parse each element
  if (Array.isArray(input)) {
    return input.map((item) => recursiveJsonParse(item));
  }

  // Case 4: If input is an object, recursively parse each property value
  if (typeof input === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};

    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        result[key] = recursiveJsonParse(input[key]);
      }
    }

    return result;
  }

  // Base case 5: For all other types (number, boolean, etc.), return as is
  return input;
}
